import { CustomerIoProvider } from '@/components/customerio/context'
import { activeCallAtom, callStore } from '@/features/interview-scheduling/call-store'
import { GlobalCallDock } from '@/features/interview-scheduling/global-call-dock'
import { useAuth } from '@/lib'
import type { RootContext } from '@/types'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { createRootRouteWithContext, Outlet } from '@tanstack/react-router'
import { TanStackRouterDevtools } from '@tanstack/router-devtools'
import posthog from 'posthog-js'
import { useEffect } from 'react'
import { AssetHubManager, DesignGalleryManager, FontPickerManager } from '../managers'
import { logger, ToastProvider } from '../ui'

export const Route = createRootRouteWithContext<RootContext>()({
	component: RootComponent,
})

function RootComponent() {
	const isDev = import.meta.env.VITE_ENVIRONMENT === 'development'
	const { isSignedIn, user } = useAuth()

	useEffect(() => {
		if (isSignedIn && user) {
			const currentUserId = posthog.get_distinct_id()
			if (currentUserId !== user.id) {
				logger.identify({
					id: user.id,
					email: user.email,
				})
			}
		}
	}, [isSignedIn, user])

	// Clear the app-wide call when the session ends so a stale call URL + candidate identifiers
	// can't remount under the next user if they sign in without a full page reload.
	useEffect(() => {
		if (!isSignedIn) {
			callStore.set(activeCallAtom, null)
		}
	}, [isSignedIn])

	return (
		<>
			<div style={{ contain: 'layout' }}>
				<ToastProvider>
					<FontPickerManager>
						<DesignGalleryManager>
							<AssetHubManager>
								<CustomerIoProvider>
									<Outlet />
								</CustomerIoProvider>
							</AssetHubManager>
						</DesignGalleryManager>
					</FontPickerManager>

					{isDev
						? (
							<>
								<ReactQueryDevtools buttonPosition="bottom-right" />
								<TanStackRouterDevtools position="bottom-left" />
							</>
						)
						: null}
				</ToastProvider>
			</div>

			{isSignedIn ? <GlobalCallDock /> : null}
		</>
	)
}
